<template>
  <div :class="$style.addressDetails">
    <h2>{{ $t('details.ADDRESS') }}</h2>

    <address :class="$style.address">
      <span v-if="street" :class="$style.street">{{ street }}, </span>
      <span>{{ zipCode }} {{ city }}</span>
    </address>

    <div v-if="isMapVisible" :class="$style.mapWrapper">
      <HgLoadWhenIdle :delayMs="1000" :class="$style.loadWhenIdleWrapper">
        <iframe :class="$style.map" :src="embeddedMapUrl" />
      </HgLoadWhenIdle>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgLoadWhenIdle } from '@smg-real-estate/domus';
import { Accuracy, Address } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';

import { getStreetWithAddition } from '@/helpers/ListingData/ListingData';
import { useI18n } from 'vue-i18n';

const GOOGLE_MAPS_BASE_URL = 'https://www.google.com/maps/embed/v1';
const accuracyToZoomMap: Record<Accuracy, number> = {
  MIN: 14,
  LOW: 14,
  MEDIUM: 15,
  HIGH: 16,
};

const { locale } = useI18n();

defineOptions({
  name: 'AddressDetails',
});

const props = defineProps<{
  address: Address;
}>();

const latitude = computed((): number | undefined => {
  return props.address?.geoCoordinates?.latitude;
});

const longitude = computed((): number | undefined => {
  return props.address?.geoCoordinates?.longitude;
});

const street = computed((): string | undefined => {
  return getStreetWithAddition(props.address);
});

const zipCode = computed(() => {
  return props.address.postalCode ?? '';
});

const city = computed((): string => {
  return props.address.locality ?? '';
});

const isMapVisible = computed((): boolean => {
  return latitude.value !== undefined && longitude.value !== undefined;
});

const embeddedMapUrl = computed(() => {
  // Documentation for the Google Maps embedded map API:
  // https://developers.google.com/maps/documentation/embed/guide

  const accuracy = props.address?.geoCoordinates?.accuracy;
  const mapZoomLevel = (accuracy && accuracyToZoomMap[accuracy]) || accuracyToZoomMap.MIN;
  const isHighOrMediumAccuracy = accuracy && [Accuracy.High, Accuracy.Medium].includes(accuracy);

  if (isHighOrMediumAccuracy) {
    return `${GOOGLE_MAPS_BASE_URL}/place?key=${import.meta.env.VUE_GOOGLE_MAPS_PDP_API_KEY}&q=${latitude.value},${longitude.value}&zoom=${mapZoomLevel}&language=${locale.value}`;
  } else {
    return `${GOOGLE_MAPS_BASE_URL}/view?key=${import.meta.env.VUE_GOOGLE_MAPS_PDP_API_KEY}&center=${latitude.value},${longitude.value}&zoom=${mapZoomLevel}&language=${locale.value}`;
  }
});
</script>

<style module lang="scss">
@import '@/assets/variables';

$map-height: 240px;
$cta-no-address-button-height: $space-200;
$cta-no-address-button-desktop-max-width: 274px;

.addressDetails {
  width: 100%;

  h2 {
    margin-bottom: $space-25;
  }

  .address {
    padding-top: $space-75;
    padding-bottom: $space-75;
    font-style: normal;
    hyphens: auto;
    word-break: break-word;
    overflow-wrap: break-word;

    .street {
      font-weight: bold;
    }
  }

  .mapWrapper {
    position: relative;
    height: $map-height;
    background-color: $color-grey-50;

    .loadWhenIdleWrapper {
      width: 100%;
      height: 100%;

      .map {
        width: 100%;
        height: 100%;
        border: none;
      }
    }
  }
}
</style>
